<script>
	// Components
	import Modal from '@/Components/Modal.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Switch from '@/Components/Switch.svelte';
	// Stores
	import { editResultsOpen } from '@/Stores/Store.js';
	import ManualResultStore from '@/Stores/Racing/ManualResultStore.js';
	import SelectionStore from '@/Stores/Racing/SelectionStore.js';
	import RaceStore from '@/Stores/Racing/RaceStore.js';
	// Services
	import { getGetOrdinal } from '@/Services/Helpers.js';
	import { notify } from '@/Services/Notify.js';
	// Constants
	import { SelectionStatus } from '@/Constants/Selection.js';
	import { ProductNames } from '@/Constants/RaceProducts.js';
	import { BetType, BetTypeNames, ExoticBetTypes } from '@/Constants/Bet.js';
	import Icon from '@/Components/Icon.svelte';

	export let meeting;
	export let race;

	$: $editResultsOpen && reset();

	let sending = false;
	let manualResult;
	let errors;

	function reset() {
		ManualResultStore.setupResults(race.number_of_places, race);
		manualResult = race && race.manual_result ? true : false;
	}

	function resetExotics() {
		ManualResultStore.clearExotics();
	}
	function resetForm() {
		ManualResultStore.setupResults(race.number_of_places, race, false, false, false);
	}

	function updateManualResult() {
		RaceStore.updateManualResult(race.id, {
			manual_result: true,
			in_review: true
		})
			.then(() => {
				notify({
					type: 'success',
					title: `Race ${race.race_number}`,
					message: 'Manual Result turned ON',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: `Race ${race.race_number}`,
					message: (e.error && e.error[0] && e.error[0].detail) || 'Failed to update manual result',
					timeout: 3
				});
			});
	}

	function updateSelection(place, selectionId) {
		ManualResultStore.updateResultSelection(
			place.id,
			selectionId,
			$SelectionStore.where('id', selectionId).pluck('number').first()
		);

		if (place.position <= race.number_of_places) {
			ManualResultStore.updateDividendsFromSelection(place, selectionId);
		}
	}

	function checkFormIsValid() {
		errors = {};
		for (const place of $ManualResultStore
			.where('bet_type_id', BetType.Place)
			.where('position', '<=', 4)
			.sortBy('position')
			.all()) {
			if (place.position <= race.number_of_places && place.selection_ids?.length == 0) {
				errors['place.' + place.position + '.' + place.id] = ['This placing is required'];
			}
		}
		for (const place of $ManualResultStore.all()) {
			for (const dividend of place.dividends) {
				if (dividend.value && dividend.value !== 0 && dividend.value < 100) {
					errors['place.' + place.position + '.' + place.id + '.dividend'] = ['Must be either 0 or >= 1'];
				}
				if (ExoticBetTypes.includes(dividend.bet_type_id)) {
					if (dividend.value && dividend.value >= 100 && !dividend.prize_pool) {
						errors['place.' + place.position + '.' + place.id + '.dividend_pool'] = ['Prize pool is required'];
					}
				}
			}
		}

		return Object.keys(errors).length === 0;
	}

	function getSelectionIdForPlace(place) {
		if (place.selection_ids[0]) {
			return place.selection_ids[0];
		}

		if (place.duplicate !== false) {
			return undefined;
		}

		const positionSelection = $SelectionStore
			.where('race_id', race.id)
			.where('position', parseInt(place.position.toString()))
			.first();

		if (
			positionSelection &&
			$ManualResultStore
				.where('bet_type_id', BetType.Place)
				.where('position', '<=', 4)
				.where('selection_ids', '!=', [positionSelection.id])
				.sortBy('position')
				.count() == 0
		) {
			return positionSelection.id;
		}

		return undefined;
	}
	function saveResults() {
		if (!checkFormIsValid()) {
			return false;
		}

		sending = true;
		ManualResultStore.saveResults(race)
			.then(() => {
				notify({
					type: 'success',
					title: 'Manual Results Saved',
					message: `Manual Results updated for Race ${race.race_number}.`,
					timeout: 3
				});
				editResultsOpen.set(false);
				RaceStore.getRaceWithDetails(race.id, meeting.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Manual Results Failed',
					message:
						(e.error && e.error[0]) ||
						(e.results &&
							e.results[0] &&
							e.results[0] == 'The results field is required.' &&
							'Not all required fields have been filled out') ||
						'Problem saving results',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
			});
	}
</script>

{#if $editResultsOpen && race}
	<Modal on:close={() => editResultsOpen.set(false)} className="mx-2 md:w-5/6 md:mx-auto">
		<div class="pt-2 font-semibold text-center uppercase" slot="header">
			Manual Result {meeting.name} - Race {race.race_number}
		</div>
		<p class="pt-1 text-sm italic text-center">
			Pays {race.number_of_places} places
		</p>
		{#if !race.manual_result}
			<div class="w-full py-4 mt-4 text-center border-t">
				Turn on Manual Resulting
				<Switch
					on:change={updateManualResult}
					disabled={manualResult}
					name={'manual_result' + race.race_number}
					bind:checked={manualResult}
				/>
				<div class="pt-2 font-semibold text-red-500">
					<p>Warning: once you turn on manual resulting, you must enter the results manually to process any bets.</p>
					<p>This will also turn on "Manual Review" for this race.</p>
				</div>
			</div>
		{:else if race.in_review}
			<div class="flex w-full px-2 mt-4 space-x-2">
				<!-- PLACINGS -->
				<div class="grid w-1/2 h-full gap-2 places-grid">
					<!-- HEADER -->
					<div class="col-start-2 font-semibold">Selection</div>
					<div class="text-center">&#x250C</div>
					<div class="font-semibold text-center">Win</div>
					<div class="text-center">&#x2510</div>
					<div class="text-center">&#x250C</div>
					<div class="font-semibold text-center">Place</div>
					<div class="text-center">&#x2510</div>
					{#each $ManualResultStore
						.where('bet_type_id', BetType.Place)
						.where('position', '<=', 4)
						.sortBy('position')
						.all() as place (place.id)}
						<!-- POSITION -->
						<div class="self-start mt-3 col-start-1 text-sm text-right">
							{getGetOrdinal(place.position)}
						</div>
						{#key place.position + '.' + place.id + '.' + place.selection_ids.flat()}
							<!-- SELECTION LIST-->
							<SelectInput
								className="w-full"
								name={'place.' + place.position + '.' + place.id}
								errors={errors && errors['place.' + place.position + '.' + place.id]}
								value={getSelectionIdForPlace(place)}
								on:change={(e) => updateSelection(place, e.target.value)}
							>
								<option value={undefined}>Select...</option>
								{#each $SelectionStore
									.where('race_id', race.id)
									.where('status.id', SelectionStatus.Active)
									.sortBy('number')
									.all() as selection (selection.id)}
									<option
										value={selection.id}
										disabled={$ManualResultStore
											.where('bet_type_id', BetType.Place)
											.where('position', '<=', 4)
											.pluck('selection_ids')
											.collapse()
											.all()
											.includes(selection.id)}>{selection.number}. {selection.name}</option
									>
								{/each}
							</SelectInput>
						{/key}
						<!-- DIVIDENDS -->
						{#if place.position !== '1'}
							<div class="col-span-3" />
						{/if}
						{#if place.position <= race.number_of_places}
							{#each place.dividends as dividend, index (index)}
								<div class="flex flex-row justify-center items-start">
									<div class="relative">
										<div class="flex flex-col items-start justify-center">
											<input
												type="number"
												class="px-1 text-xs text-center form form-input focus:outline-none border-gray-200 pt-3.5"
												value={dividend.value / 100 || null}
												on:change={(e) => (dividend.value = Math.round(parseFloat(e.target.value).toFixed(2) * 100))}
											/>
											<span
												class="absolute w-[calc(100%-2px)] text-center bg-gray-200 rounded-t-sm text-tiny border border-transparent top-px left-px right-px select-none"
											>
												{ProductNames[dividend.product_id]}
											</span>
											{#if dividend.value && dividend.value !== 0 && dividend.value < 100}
												<div class="form-error">Must be 0 or > 1</div>
											{/if}
										</div>
									</div>
								</div>
							{/each}
						{:else}
							<div class="col-span-3" />
						{/if}
						{#if place.duplicate}
							<button
								class="self-start mt-2.5 w-6 h-6 col-start-9 text-white bg-red-500 rounded-full focus:outline-none hover:bg-red-600"
								on:click={() => ManualResultStore.removeResultForId(place.id, place.position)}
							>
								<Icon name="minus" classes="p-1" />
							</button>
							<!-- ONLY ALLOW 1 DUPLICATE AT A TIME -->
						{:else if $ManualResultStore.where('duplicate', true).where('bet_type_id', BetType.Place).count() === 0}
							<button
								class="self-start mt-2.5 w-6 h-6 col-start-9 text-white bg-gray-400 rounded-full hover:bg-green-500 focus:outline-none"
								on:click={() => ManualResultStore.addEmptyResultForPosition(place.position)}
							>
								<Icon name="plus" classes="p-1" />
							</button>
						{/if}
					{/each}
				</div>
				<!-- EXOTICS -->
				<div class="grid w-1/2 h-full gap-2 exotics-grid">
					<!-- HEADER -->
					<div class="font-semibold text-right">Exotic</div>
					<div class="col-span-2 font-semibold text-center">&#x250C NSW &#x2510</div>
					<div class="col-span-2 font-semibold text-center">&#x250C SUP &#x2510</div>
					<div class="col-span-2 font-semibold text-center">&#x250C TATTS &#x2510</div>
					{#each $ManualResultStore
						.whereIn('bet_type_id', [BetType.Quinella, BetType.Exacta, BetType.Trifecta, BetType.FirstFour])
						.sortBy('bet_type_id')
						.all() as exotic (exotic.id)}
						<!-- EXOTIC TYPE -->
						<div class="self-center col-start-1 text-sm text-right">
							<div>{BetTypeNames[exotic.bet_type_id]}</div>
							<div>{exotic.result_string}</div>
						</div>
						<!-- DIV/POOL -->
						{#each exotic.dividends as dividend, index (index)}
							<div class="relative">
								<div class="flex flex-col items-center justify-center">
									<input
										type="number"
										class="px-1 text-xs text-center form form-input focus:outline-none border-gray-200 pt-3.5"
										value={dividend.value / 100 || null}
										on:change={(e) => (dividend.value = Math.round(parseFloat(e.target.value).toFixed(2) * 100))}
									/>
									<span
										class="absolute w-[calc(100%-2px)] text-center bg-gray-200 rounded-t-sm text-tiny border border-transparent top-px left-px right-px select-none"
									>
										DIV
									</span>
									{#if dividend.value && dividend.value !== 0 && dividend.value < 100}
										<div class="form-error">Must be 0 or > 1</div>
									{/if}
								</div>
							</div>
							<div class="relative">
								<div class="flex flex-col items-center justify-center">
									<input
										type="number"
										class="px-1 text-xs text-center form form-input focus:outline-none border-gray-200 pt-3.5"
										value={dividend.prize_pool / 100 || null}
										on:change={(e) => (dividend.prize_pool = Math.round(parseFloat(e.target.value).toFixed(2) * 100))}
									/>
									<span
										class="absolute w-[calc(100%-2px)] text-center bg-gray-200 rounded-t-sm text-tiny border border-transparent top-px left-px right-px select-none"
									>
										POOL
									</span>
									{#if dividend.value && !dividend.prize_pool}
										<div class="form-error">Pool required</div>
									{/if}
								</div>
							</div>
						{/each}
					{/each}
				</div>
			</div>
			<div class="flex items-center justify-between">
				<div class="flex items-center justify-start mt-4 space-x-2">
					<button on:click={() => resetForm()} class="btn btn-danger"> Reset form</button>
					<button on:click={() => resetExotics()} class="btn btn-danger"> Clear Exotics</button>
				</div>
				<div class="flex items-center justify-end mt-4 space-x-2">
					<p class="mr-2 text-sm italic font-semibold text-red-500">
						Note: You can keep updating the results until correct.
						<br />
						You must TURN OFF manual review to start paying out bets.
					</p>
					<button on:click={() => editResultsOpen.set(false)} class="btn btn-primary-action"> Cancel</button>
					<LoadingButton name="submitManualResults" loading={sending} className="btn btn-black" on:click={saveResults}
						>Save</LoadingButton
					>
				</div>
			</div>
		{:else}
			<div class="py-4 text-center">This race is currently paying out bets and can't be updated any further.</div>
		{/if}
	</Modal>
{/if}

<style>
	.places-grid {
		grid-template-columns: 0.5fr 3fr repeat(6, 1fr) 0.5fr;
	}

	.exotics-grid {
		grid-template-columns: 2fr repeat(6, 1fr);
	}
</style>
